import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import DashboardLayout from '../DashboardLayout';

const ReportesManagement = () => {
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  // Cargar datos para reportes
  const loadData = async () => {
    try {
      setLoading(true);
      const empleadosData = await adminAPI.getEmpleados({ per_page: 100 });
      setEmpleados(empleadosData?.items || []);
      
      // Calcular estadísticas
      const empleadosArray = empleadosData?.items || [];
      const totalEmpleados = empleadosArray.length;
      const empleadosActivos = empleadosArray.filter(e => e.activo).length;
      const empleadosInactivos = totalEmpleados - empleadosActivos;
      
      // Agrupar por departamento
      const porDepartamento = empleadosArray.reduce((acc, emp) => {
        const dept = emp.departamento || 'Sin departamento';
        acc[dept] = (acc[dept] || 0) + 1;
        return acc;
      }, {});
      
      // Agrupar por cargo
      const porCargo = empleadosArray.reduce((acc, emp) => {
        const cargo = emp.cargo || 'Sin cargo';
        acc[cargo] = (acc[cargo] || 0) + 1;
        return acc;
      }, {});
      

      setStats({
        totalEmpleados,
        empleadosActivos,
        empleadosInactivos,
        porDepartamento,
        porCargo,
        departamentos: Object.keys(porDepartamento).length,
        cargosUnicos: Object.keys(porCargo).length
      });
      
    } catch (error) {
      toast.error('Error al cargar datos para reportes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Exportar reporte a CSV
  const exportToCSV = () => {
    const csvContent = [
      ['Nombre', 'Documento', 'Cargo', 'Departamento', 'Email', 'Estado'],
      ...empleados.map(emp => [
        emp.nombre_completo || '',
        emp.documento_identidad || '',
        emp.cargo || '',
        emp.cliente || '',
        emp.email || '',
        emp.activo ? 'Activo' : 'Inactivo'
      ])
    ].map(row => row.join(',')).join('\\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_empleados_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Reporte exportado exitosamente');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">Reportes y Estadísticas</h1>
            <p className="mt-2 text-sm text-gray-700">
              Análisis detallado de empleados y departamentos
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              onClick={exportToCSV}
            >
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Estadísticas generales */}
        <div className="mt-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Resumen General</h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Empleados</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.totalEmpleados || 0}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Empleados Activos</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.empleadosActivos || 0}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Departamentos</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.departamentos || 0}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 112 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 112-2z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Cargos Únicos</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.cargosUnicos || 0}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Distribución por departamento */}
        <div className="mt-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Distribución por Departamento</h3>
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                {Object.entries(stats.porDepartamento || {}).map(([dept, count]) => (
                  <div key={dept} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{dept}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${(count / stats.totalEmpleados) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="ml-4 text-sm text-gray-500">
                      {count} ({Math.round((count / stats.totalEmpleados) * 100)}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Top 10 cargos */}
        <div className="mt-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Top 10 Cargos más Comunes</h3>
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                {Object.entries(stats.porCargo || {})
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 10)
                  .map(([cargo, count]) => (
                  <div key={cargo} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{cargo}</div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${(count / stats.totalEmpleados) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="ml-4 text-sm text-gray-500">
                      {count} empleado{count !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Estado de empleados */}
        <div className="mt-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Estado de Empleados</h3>
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{stats.empleadosActivos || 0}</div>
                  <div className="text-sm text-gray-500">Empleados Activos</div>
                  <div className="mt-2">
                    <div className="text-xs text-gray-400">
                      {stats.totalEmpleados ? Math.round((stats.empleadosActivos / stats.totalEmpleados) * 100) : 0}% del total
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">{stats.empleadosInactivos || 0}</div>
                  <div className="text-sm text-gray-500">Empleados Inactivos</div>
                  <div className="mt-2">
                    <div className="text-xs text-gray-400">
                      {stats.totalEmpleados ? Math.round((stats.empleadosInactivos / stats.totalEmpleados) * 100) : 0}% del total
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReportesManagement;